// Simplified questionnaire data structure

export const questionnaireData = {
  sections: [
    // ---------------------------------------------------------------------
    // 1. Your Situation
    // ---------------------------------------------------------------------
    {
      id: 'section1',
      title: 'Your Situation',
      description: 'Quick context setting',
      questions: [
        {
          id: 'q1_situation',
          type: 'single-choice',
          question: 'Tell us a bit about where you are right now…',
          required: true,
          options: [
            { value: 'already_in_kl', label: "We're already in KL" },
            { value: 'moving_to_kl', label: "We're moving to KL from overseas" }
          ]
        },
        {
          // q2_name lives here now — shown immediately after q1_situation for every parent
          id: 'q2_name',
          type: 'text',
          question: "What should we call your child?",
          placeholder: 'First name is fine',
          required: true
        },
        {
          id: 'q1_country_moving_from',
          type: 'single-choice',
          displayMode: 'capsule',
          question: 'Which country are you moving from?',
          required: true,
          conditionalDisplay: { dependsOn: 'q1_situation', showWhen: ['moving_to_kl'] },
          options: [
            { value: 'singapore', label: 'Singapore' },
            { value: 'japan', label: 'Japan' },
            { value: 'south_korea', label: 'South Korea' },
            { value: 'china', label: 'China' },
            { value: 'hong_kong', label: 'Hong Kong' },
            { value: 'india', label: 'India' },
            { value: 'australia', label: 'Australia' },
            { value: 'uk', label: 'United Kingdom' },
            { value: 'usa', label: 'United States' },
            { value: 'canada', label: 'Canada' },
            { value: 'middle_east', label: 'Middle East' },
            { value: 'europe', label: 'Europe (Other)' },
            { value: 'other', label: 'Other' }
          ]
        },
        {
          id: 'q1_school_level',
          type: 'single-choice',
          question: 'Which level is {childName} at the moment?',
          required: true,
          options: [
            { value: 'primary', label: 'Primary' },
            { value: 'secondary', label: 'Secondary' },
            { value: 'not_started', label: 'Has not yet started school' }
          ]
        },
        {
          id: 'q1_school_type',
          type: 'single-choice',
          question: 'What kind of school does {childName} go to?',
          required: true,
          conditionalDisplay: { dependsOn: 'q1_school_level', showWhen: ['primary', 'secondary'] },
          options: [
            { value: 'international', label: 'International School', subtext: 'IB, Cambridge, American etc.' },
            { value: 'private', label: 'Private', subtext: 'Local private institution' },
            { value: 'national', label: 'Government / National School', subtext: 'Public schooling system' },
            { value: 'other', label: 'Other', subtext: 'Homeschooling etc.' }
          ]
        },
        {
          id: 'q1_school_type_new_student',
          type: 'single-choice',
          question: 'What kind of school are you looking for?',
          required: true,
          conditionalDisplay: { dependsOn: 'q1_school_level', showWhen: ['not_started'] },
          options: [
            { value: 'international', label: 'International School', subtext: 'IB, Cambridge, American etc.' },
            { value: 'private', label: 'Private', subtext: 'Local private institution' },
            { value: 'national', label: 'Government / National School', subtext: 'Public schooling system' },
            { value: 'other', label: 'Other', subtext: 'Homeschooling etc.' }
          ]
        },
        {
          id: 'q1_current_school_international_kl',
          type: 'autocomplete',
          question: 'What school is {childName} currently in?',
          placeholder: 'Start typing school name...',
          required: true,
          apiEndpoint: '/api/schools/list',
          conditionalDisplay: {
            dependsOn: ['q1_situation', 'q1_school_type'],
            showWhen: { q1_situation: ['already_in_kl'], q1_school_type: ['international'] }
          }
        },
        {
          id: 'q1_current_school_international_overseas',
          type: 'text',
          question: 'What school does {childName} currently attend?',
          placeholder: 'Enter school name...',
          required: true,
          conditionalDisplay: {
            dependsOn: ['q1_situation', 'q1_school_type'],
            showWhen: { q1_situation: ['moving_to_kl'], q1_school_type: ['international'] }
          }
        },
        {
          id: 'q1_current_school_other_kl',
          type: 'text',
          question: 'What school is {childName} currently in?',
          placeholder: 'Enter school name...',
          required: true,
          conditionalDisplay: {
            dependsOn: ['q1_situation', 'q1_school_type'],
            showWhen: { q1_situation: ['already_in_kl'], q1_school_type: ['private', 'national', 'other'] }
          }
        },
        {
          id: 'q1_current_school_other_overseas',
          type: 'text',
          question: 'What school does {childName} currently attend?',
          placeholder: 'Enter school name...',
          required: true,
          conditionalDisplay: {
            dependsOn: ['q1_situation', 'q1_school_type'],
            showWhen: { q1_situation: ['moving_to_kl'], q1_school_type: ['private', 'national', 'other'] }
          }
        },
        {
          id: 'q1_grade_new_student',
          type: 'single-choice',
          question: 'What grade will they be entering?',
          required: true,
          conditionalDisplay: { dependsOn: 'q1_school_level', showWhen: ['not_started'] },
          options: [
            { value: 1, label: 'Year 1', subtext: 'Age 6-7' }
          ]
        },
        {
          id: 'q1_grade_primary',
          type: 'single-choice',
          displayMode: 'compact',
          question: 'What grade is {childName} entering?',
          required: true,
          conditionalDisplay: { dependsOn: 'q1_school_level', showWhen: ['primary'] },
          options: [
            { value: 1, label: 'Year 1', subtext: 'Age 6-7' },
            { value: 2, label: 'Year 2', subtext: 'Age 7-8' },
            { value: 3, label: 'Year 3', subtext: 'Age 8-9' },
            { value: 4, label: 'Year 4', subtext: 'Age 9-10' },
            { value: 5, label: 'Year 5', subtext: 'Age 10-11' },
            { value: 6, label: 'Year 6', subtext: 'Age 11-12' }
          ]
        },
        {
          id: 'q1_grade_secondary',
          type: 'single-choice',
          displayMode: 'compact',
          question: 'What grade is {childName} entering?',
          required: true,
          conditionalDisplay: { dependsOn: 'q1_school_level', showWhen: ['secondary'] },
          options: [
            { value: 7, label: 'Year 7' },
            { value: 8, label: 'Year 8' },
            { value: 9, label: 'Year 9' },
            { value: 10, label: 'Year 10', subtext: 'IGCSE Y1' },
            { value: 11, label: 'Year 11', subtext: 'IGCSE Y2' },
            { value: 12, label: 'Year 12', subtext: 'A-Level / IB Y1' },
            { value: 13, label: 'Year 13', subtext: 'A-Level / IB Y2' }
          ]
        }
      ]
    },

    // ---------------------------------------------------------------------
    // 2. About [child's name] — full section only for moving_to_kl.
    //    q2_name has already been collected in section 1; the remaining
    //    questions (academic, subjects, interests, social) only appear when
    //    the parent is new to KL so we don't interrogate local parents.
    // ---------------------------------------------------------------------
    {
      id: 'section2',
      title: 'About {childName}',
      description: 'Child profile for matching',
      questions: [
        {
          id: 'q2_academic',
          type: 'single-choice',
          question: 'How is {childName} doing academically?',
          required: true,
          conditionalDisplay: {
            dependsOn: ['q1_situation', 'q1_school_level'],
            showWhen: {
              q1_situation: ['moving_to_kl'],
              q1_school_level: ['primary', 'secondary']
            }
          },
          options: [
            { value: 'top_performer', label: 'Thriving and really enjoying learning' },
            { value: 'doing_well', label: 'Doing well overall' },
            { value: 'middle', label: 'Steady and finding their rhythm' },
            { value: 'struggling', label: 'Finding some areas challenging' },
            { value: 'mixed', label: 'A mix — strong in some areas, needs support in others' }
          ]
        },
        {
          id: 'q2_strongest_subjects',
          type: 'multi-select',
          question: "Where do you see {childName} really shine?",
          subtitle: 'Select all that apply',
          required: false,
          conditionalDisplay: {
            dependsOn: ['q1_situation', 'q1_school_level'],
            showWhen: {
              q1_situation: ['moving_to_kl'],
              q1_school_level: ['primary', 'secondary']
            }
          },
          options: [
            { value: 'math', label: 'Math' },
            { value: 'science', label: 'Science' },
            { value: 'english', label: 'English / Languages' },
            { value: 'humanities', label: 'Humanities (History, Geography)' },
            { value: 'arts', label: 'Arts (Visual, Music, Drama)' },
            { value: 'sports', label: 'Sports / PE' }
          ]
        },
        {
          id: 'q2_interests',
          type: 'multi-select',
          question: "What are {childName}'s interests & passions?",
          subtitle: 'Select all that apply',
          required: false,
          conditionalDisplay: {
            dependsOn: ['q1_situation', 'q1_school_level'],
            showWhen: {
              q1_situation: ['moving_to_kl'],
              q1_school_level: ['primary', 'secondary']
            }
          },
          options: [
            { value: 'sports', label: 'Sports & athletics' },
            { value: 'arts', label: 'Visual arts & design' },
            { value: 'music', label: 'Music (instrument or singing)' },
            { value: 'drama', label: 'Drama & performance' },
            { value: 'stem', label: 'Coding & technology' },
            { value: 'science', label: 'Science experiments' },
            { value: 'languages', label: 'Languages & communication' },
            { value: 'leadership', label: 'Leadership & service' },
            { value: 'reading', label: 'Reading & writing' }
          ]
        },
        {
          id: 'q2_social',
          type: 'single-choice',
          question: 'How would you describe {childName} socially?',
          required: true,
          conditionalDisplay: {
            dependsOn: 'q1_situation',
            showWhen: ['moving_to_kl']
          },
          options: [
            { value: 'confident', label: 'Very social and makes friends easily' },
            { value: 'few_friends', label: 'Has a small circle of close friends' },
            { value: 'introverted', label: 'More comfortable in quieter or smaller groups' },
            { value: 'struggles', label: 'Could use more support socially' }
          ]
        }
      ]
    },

    // ---------------------------------------------------------------------
    // 3. What's working — and what's not (Q15 / Q16)
    //    Only relevant for parents already in KL with a child enrolled.
    // ---------------------------------------------------------------------
    {
      id: 'section3',
      title: "What's working — and what's not",
      description: "What's working and what's not",
      questions: [
        {
          id: 'q15_whats_working',
          type: 'multi-select',
          question: "What's genuinely working at {childName}'s current school?",
          subtitle: 'Pick up to 5',
          required: true,
          maxSelect: 5,
          conditionalDisplay: {
            dependsOn: ['q1_situation', 'q1_school_level'],
            showWhen: {
              q1_situation: ['already_in_kl'],
              q1_school_level: ['primary', 'secondary']
            }
          },
          options: [
            { value: 'high_academic_standards',     label: 'Strong teaching, good academic results' },
            { value: 'child_feels_safe_supported',  label: 'Teachers really care' },
            { value: 'teachers_know_child',         label: 'Teachers know my child' },
            { value: 'school_size_right',           label: 'Right school size — not too big' },
            { value: 'diverse_international',       label: 'Good mix of nationalities' },
            { value: 'strong_university_placements',label: 'Good university results' },
            { value: 'wide_range_activities',       label: 'Lots of activities and clubs' },
            { value: 'curriculum_suits_well',       label: 'Curriculum suits my child' },
            { value: 'language_development',        label: 'Good language support' },
            { value: 'fees_fair_value',             label: 'Fees are worth it' }
          ]
        },
        {
          id: 'q16_not_working',
          type: 'multi-select',
          question: "What's not working at {childName}'s current school – or what's missing?",
          subtitle: 'Tap any words that fit. You can also type your own. Select as many as you like.',
          required: true,
          allowCustom: true,
          conditionalDisplay: {
            dependsOn: ['q1_situation', 'q1_school_level'],
            showWhen: {
              q1_situation: ['already_in_kl'],
              q1_school_level: ['primary', 'secondary']
            }
          },
          options: [
            { value: 'not_challenged_enough',     label: 'Not challenged enough' },
            { value: 'teachers_keep_leaving',     label: 'Teachers keep leaving' },
            { value: 'no_individual_attention',   label: 'Child gets lost in the crowd' },
            { value: 'too_big_impersonal',        label: 'Too big / impersonal' },
            { value: 'lacks_diversity',           label: 'Not diverse enough' },
            { value: 'weak_university_guidance',  label: 'Unsure about university support' },
            { value: 'not_enough_activities',     label: 'Not enough activities' },
            { value: 'curriculum_doesnt_fit',     label: "Curriculum doesn't fit" },
            { value: 'bad_communication',         label: 'Bad communication' },
            { value: 'fees_not_justified',        label: 'Not worth the fees' },
            { value: 'very_strict_teachers',      label: 'Very strict teachers' },
            { value: 'not_future_ready',          label: 'Not future-ready' },
            { value: 'too_much_homework',         label: 'Too much homework / pressure' },
            { value: 'too_exam_focused',          label: 'Too exam-focused' },
            { value: 'not_enough_mandarin',       label: 'Not enough Mandarin / mother tongue support' }
          ]
        }
      ]
    },

    // ---------------------------------------------------------------------
    // 4. What matters in the next school (Q17)
    // ---------------------------------------------------------------------
    {
      id: 'section3_priorities',
      title: 'What matters in the next school',
      description: 'What matters most in your next school',
      questions: [
        {
          id: 'q17_priorities',
          type: 'numbered-select',
          question: "Let's figure this out together — what matters most to you in a school?",
          subtitle: 'Pick and rank up to 5 priorities',
          required: true,
          minSelect: 3,
          maxSelect: 5,
          options: [
            { value: 'strong_academic_rigour',          label: 'Strong academics — high standards and rigorous teaching' },
            { value: 'child_feels_known_supported',     label: "Pastoral care — {childName} won't get lost in the crowd" },
            { value: 'excellent_teachers_stay',         label: 'Stable, experienced teachers who know their students' },
            { value: 'clear_university_placement',      label: 'Strong track record for top university placements' },
            { value: 'diverse_international_community', label: 'A genuinely diverse, international student body' },
            { value: 'curriculum_for_world_ahead',      label: 'Strong STEM, technology and future-skills programme' },
            { value: 'strong_cocurricular',             label: 'Rich life beyond the classroom — sports, arts, debate' },
            { value: 'excellent_english_programme',     label: 'Excellent English language programme' },
            { value: 'reasonable_fees_value',           label: 'Good value for the fees' },
            { value: 'close_to_home',                   label: 'Close to home' }
          ]
        }
      ]
    },

    // ---------------------------------------------------------------------
    // 5. Practical details (curriculum, budget, location)
    // ---------------------------------------------------------------------
    {
      id: 'section2_current_school',
      title: 'Practical details (curriculum, budget, location)',
      description: "We're almost there. Just a few practical questions",
      questions: [
        {
          id: 'q18_university',
          type: 'multi-select',
          question: 'Have you started thinking about university destinations?',
          subtitle: "It's absolutely ok if you haven't.",
          required: false,
          conditionalDisplay: { dependsOn: 'q1_school_level', showWhen: ['secondary'] },
          options: [
            { value: 'UK', label: 'United Kingdom' },
            { value: 'US', label: 'United States' },
            { value: 'Australia', label: 'Australia' },
            { value: 'Canada', label: 'Canada' },
            { value: 'Malaysia', label: 'Malaysia' },
            { value: 'Singapore', label: 'Singapore' },
            { value: 'Europe', label: 'Europe (other)' },
            { value: 'Asia', label: 'Asia (other)' },
            { value: 'undecided', label: 'Still deciding' }
          ]
        },
        {
          id: 'q19_curriculum',
          type: 'single-choice',
          question: 'Do you have a curriculum preference?',
          required: true,
          options: [
            { value: 'ib_or_cambridge', label: 'IB or Cambridge', subtext: 'Open to both pathways' },
            { value: 'ib', label: 'IB (International Baccalaureate)', subtext: 'PYP, MYP, DP · inquiry-based, holistic' },
            { value: 'cambridge', label: 'Cambridge / British', subtext: 'IGCSE, A-Levels · traditional, exam-focused' },
            { value: 'american', label: 'American Curriculum', subtext: 'AP, SAT · flexible, broad-based' },
            { value: 'australian', label: 'Australian Curriculum', subtext: 'ATAR, VCE · balanced approach' },
            { value: 'flexible', label: 'Flexible / No strong preference', subtext: 'School quality over curriculum' }
          ]
        },
        {
          id: 'q20_budget',
          type: 'single-choice',
          displayMode: 'compact',
          question: 'What kind of fee range are you comfortable exploring?',
          required: true,
          options: [
            { value: 'under_30k', label: 'Under RM30,000' },
            { value: '30_50k', label: 'RM30,000 – RM50,000' },
            { value: '50_80k', label: 'RM50,000 – RM80,000' },
            { value: 'above_80k', label: 'Above RM80,000' },
            { value: 'flexible', label: 'Flexible if the school is right' }
          ]
        },
        {
          id: 'q21_location',
          type: 'single-choice',
          displayMode: 'grouped-chip',
          question: 'Is there a location that would work best for your family?',
          required: true,
          groups: [
            {
              label: 'CENTRAL KL',
              descriptor: 'Expat heartland — close to international schools and city amenities',
              options: [
                { value: 'mont_kiara', label: 'Mont Kiara / Hartamas' },
                { value: 'kl_city',    label: 'KL City Centre' },
                { value: 'ampang',     label: 'Ampang / KLCC East' },
                { value: 'cheras',     label: 'Cheras' },
              ],
            },
            {
              label: 'GREATER PJ / SUBANG',
              descriptor: "Suburban, family-friendly — Malaysia's largest international school corridor",
              options: [
                { value: 'petaling_jaya', label: 'Petaling Jaya / Damansara' },
                { value: 'subang',        label: 'Subang Jaya / USJ' },
                { value: 'puchong',       label: 'Puchong' },
              ],
            },
            {
              label: 'NORTH & WEST',
              descriptor: 'Quieter residential areas and newer townships',
              options: [
                { value: 'sungai_buloh', label: 'Sungai Buloh / Elmina' },
                { value: 'shah_alam',    label: 'Shah Alam / Setia Alam' },
                { value: 'klang',        label: 'Klang / Port Klang' },
              ],
            },
            {
              label: 'SOUTH & EAST',
              descriptor: 'More affordable, further from city centre — good for families near Putrajaya',
              options: [
                { value: 'kajang_bangi', label: 'Kajang / Bangi' },
                { value: 'putrajaya',    label: 'Putrajaya / Cyberjaya' },
                { value: 'rawang',       label: 'Rawang / Kundang' },
              ],
            },
          ],
          flexibleOption: { value: 'flexible', label: 'Flexible — depends on the school' },
          options: [
            { value: 'mont_kiara',   label: 'Mont Kiara / Hartamas' },
            { value: 'kl_city',      label: 'KL City Centre' },
            { value: 'ampang',       label: 'Ampang / KLCC East' },
            { value: 'cheras',       label: 'Cheras' },
            { value: 'petaling_jaya',label: 'Petaling Jaya / Damansara' },
            { value: 'subang',       label: 'Subang Jaya / USJ' },
            { value: 'puchong',      label: 'Puchong' },
            { value: 'sungai_buloh', label: 'Sungai Buloh / Elmina' },
            { value: 'shah_alam',    label: 'Shah Alam / Setia Alam' },
            { value: 'klang',        label: 'Klang / Port Klang' },
            { value: 'kajang_bangi', label: 'Kajang / Bangi' },
            { value: 'putrajaya',    label: 'Putrajaya / Cyberjaya' },
            { value: 'rawang',       label: 'Rawang / Kundang' },
            { value: 'flexible',     label: 'Flexible — depends on the school' },
          ],
        }
      ]
    },

    // ---------------------------------------------------------------------
    // 6. One last thing
    // ---------------------------------------------------------------------
    {
      id: 'section5',
      title: 'One last thing',
      description: '',
      questions: [
        {
          id: 'q22_final_thoughts',
          type: 'textarea-suggestions',
          question: "Stepping back for a moment — what's your biggest hope, or concern, when you think about the next school?",
          placeholder: "What would make you feel confident you've made the right decision?",
          required: true,
          minLength: 20,
          suggestions: [
            "That my child will be happy and thrive",
            "Finding the right academic level",
            "Making friends and fitting in",
            "Teachers who truly care",
            "A supportive environment",
            "Smooth transition process",
            "Value for money",
            "Clear path to university",
            "Child feels seen and heard",
            "Won't regret the decision",
            "School aligns with our values",
            "Strong parent community"
          ]
        }
      ]
    }
  ]
};

// Helper to check if a question should be displayed based on conditional logic
export const shouldDisplayQuestion = (question, answers) => {
  if (!question.conditionalDisplay) return true;

  const { dependsOn, showWhen } = question.conditionalDisplay;

  // Handle single dependency (legacy format)
  if (typeof dependsOn === 'string') {
    const dependentAnswer = answers[dependsOn];
    return showWhen.includes(dependentAnswer);
  }

  // Handle multiple dependencies (new format)
  // Format: dependsOn: ['q1', 'q2'], showWhen: { q1: ['value1'], q2: ['value2'] }
  if (Array.isArray(dependsOn)) {
    return dependsOn.every(dep => {
      const dependentAnswer = answers[dep];
      const validValues = showWhen[dep];
      return validValues && validValues.includes(dependentAnswer);
    });
  }

  return true;
};

// Helper to get filtered questions based on answers (for conditional display)
export const getFilteredQuestions = (answers) => {
  const allQuestions = [];
  questionnaireData.sections.forEach(section => {
    section.questions.forEach(question => {
      if (shouldDisplayQuestion(question, answers)) {
        allQuestions.push({ ...question, sectionTitle: section.title });
      }
    });
  });
  return allQuestions;
};

// Helper to get total question count (accounting for conditional questions)
export const getTotalQuestions = (answers = {}) => {
  return getFilteredQuestions(answers).length;
};

// Helper to get question by index (accounting for conditional questions)
export const getQuestionByIndex = (index, answers = {}) => {
  const filteredQuestions = getFilteredQuestions(answers);
  return filteredQuestions[index] || null;
};

// Helper to get section progress
export const getSectionProgress = (currentQuestionIndex, answers = {}) => {
  const filteredQuestions = getFilteredQuestions(answers);
  const totalQuestions = filteredQuestions.length;
  const currentQuestion = filteredQuestions[currentQuestionIndex];

  if (!currentQuestion) {
    return {
      sectionIndex: 0,
      sectionTitle: questionnaireData.sections[0].title,
      questionInSection: 1,
      totalInSection: 1,
      overallProgress: 0
    };
  }

  // Count questions in current section up to current index
  let questionsInSection = 0;
  let questionIndexInSection = 0;

  for (let i = 0; i <= currentQuestionIndex; i++) {
    if (filteredQuestions[i].sectionTitle === currentQuestion.sectionTitle) {
      questionIndexInSection = questionsInSection;
      questionsInSection++;
    }
  }

  // Count total questions in this section
  let totalInSection = 0;
  for (const q of filteredQuestions) {
    if (q.sectionTitle === currentQuestion.sectionTitle) {
      totalInSection++;
    }
  }

  return {
    sectionIndex: questionnaireData.sections.findIndex(s => s.title === currentQuestion.sectionTitle),
    sectionTitle: currentQuestion.sectionTitle,
    questionInSection: questionIndexInSection + 1,
    totalInSection,
    overallProgress: Math.round((currentQuestionIndex / totalQuestions) * 100)
  };
};
